import { getContrastTextColor, isLightColor, shadeColor, withAlpha } from "./color";

interface TalonarioImageParams {
  limit: number;
  taken: Set<number>;
  raffleName: string;
  accountName: string;
  accountUrl: string;
  // Se prueban en orden; deben servirse con CORS o desde el mismo dominio (requisito del canvas)
  logoUrls?: string[];
  primaryColor: string;
  formatTicket: (ticket: number) => string;
}

// Estilo oscuro: la marca se usa solo como acento
interface Palette {
  brand: string;
  onBrand: string;
  accent: string;
  glow: string;
  subtle: string;
  cellFill: string;
}

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Formato vertical de los estados de WhatsApp
const WIDTH = 1080;
const HEIGHT = 1920;
const MARGIN = 40;
const FOOTER_HEIGHT = 190;
const LEGEND_HEIGHT = 70;
const LOGO_TIMEOUT_MS = 5000;
const BACKGROUND_TOP = "#1C1C1E";
const BACKGROUND_BOTTOM = "#050505";
const CARD_FILL = "#141416";
const TEXT_PRIMARY = "#FFFFFF";
const TEXT_SECONDARY = "#B0B0B0";
const TAKEN_FILL = "#222224";
const TAKEN_TEXT = "#5C5C5C";
const STRIKE_COLOR = "#E53935";

const loadImage = (src: string) =>
  new Promise<HTMLImageElement | null>((resolve) => {
    const img = new Image();
    const timeout = setTimeout(() => resolve(null), LOGO_TIMEOUT_MS);
    img.crossOrigin = "anonymous";
    img.onload = () => {
      clearTimeout(timeout);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timeout);
      resolve(null);
    };
    img.src = src;
  });

// Primer logo que cargue; si ninguno carga se dibujan las iniciales
const loadFirstImage = async (urls: string[]) => {
  for (const url of urls) {
    const img = await loadImage(url);
    if (img) return img;
  }
  return null;
};

const buildPalette = (brand: string): Palette => ({
  brand,
  onBrand: getContrastTextColor(brand),
  // Sobre fondo oscuro las marcas oscuras se aclaran para que el acento se lea
  accent: isLightColor(brand) ? brand : shadeColor(brand, 0.35),
  glow: withAlpha(brand, 0.3),
  subtle: withAlpha(brand, 0.35),
  cellFill: withAlpha(brand, 0.1),
});

const fitFont = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number, fontSize: number, weight: string) => {
  let size = fontSize;
  ctx.font = `${weight} ${size}px sans-serif`;
  while (ctx.measureText(text).width > maxWidth && size > 20) {
    size -= 2;
    ctx.font = `${weight} ${size}px sans-serif`;
  }
};

const roundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const strike = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, lineWidth: number) => {
  ctx.strokeStyle = STRIKE_COLOR;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  ctx.moveTo(x + w * 0.15, y + h * 0.8);
  ctx.lineTo(x + w * 0.85, y + h * 0.2);
  ctx.stroke();
};

const drawCircleImage = (ctx: CanvasRenderingContext2D, img: HTMLImageElement, cx: number, cy: number, radius: number) => {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.clip();
  // "cover": recorta al cuadrado central para no deformar el logo
  const side = Math.min(img.width, img.height);
  ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, cx - radius, cy - radius, radius * 2, radius * 2);
  ctx.restore();
};

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

const drawBackground = (ctx: CanvasRenderingContext2D, palette: Palette) => {
  const gradient = ctx.createLinearGradient(0, 0, 0, HEIGHT);
  gradient.addColorStop(0, BACKGROUND_TOP);
  gradient.addColorStop(1, BACKGROUND_BOTTOM);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  // Brillo de la marca detrás del logo
  const glow = ctx.createRadialGradient(WIDTH / 2, 160, 0, WIDTH / 2, 160, 700);
  glow.addColorStop(0, palette.glow);
  glow.addColorStop(1, "rgba(0, 0, 0, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  // Anillos decorativos
  ctx.save();
  ctx.strokeStyle = palette.subtle;
  ctx.lineWidth = 3;
  ctx.globalAlpha = 0.5;
  ctx.beginPath();
  ctx.arc(WIDTH - 40, 110, 200, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(40, HEIGHT - 250, 170, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
};

const drawLogo = (ctx: CanvasRenderingContext2D, logo: HTMLImageElement | null, accountName: string, cx: number, cy: number, radius: number, palette: Palette) => {
  // Anillo con el color de la marca y resplandor
  ctx.save();
  ctx.shadowColor = palette.glow;
  ctx.shadowBlur = 45;
  ctx.fillStyle = palette.brand;
  ctx.beginPath();
  ctx.arc(cx, cy, radius + 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  if (logo) {
    drawCircleImage(ctx, logo, cx, cy, radius);
    return;
  }

  ctx.fillStyle = CARD_FILL;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = palette.accent;
  ctx.font = `bold ${Math.round(radius * 0.8)}px sans-serif`;
  ctx.fillText(getInitials(accountName), cx, cy + 4);
};

// Devuelve la coordenada Y donde termina el encabezado
const drawHeader = (
  ctx: CanvasRenderingContext2D,
  params: TalonarioImageParams,
  logo: HTMLImageElement | null,
  palette: Palette,
  compact: boolean,
) => {
  const logoRadius = compact ? 80 : 105;
  const logoY = compact ? 120 : 150;
  drawLogo(ctx, logo, params.accountName, WIDTH / 2, logoY, logoRadius, palette);

  const accountName = params.accountName.toUpperCase();
  let cursorY = logoY + logoRadius + (compact ? 45 : 60);
  ctx.fillStyle = palette.accent;
  fitFont(ctx, accountName, WIDTH - MARGIN * 2, compact ? 30 : 34, "600");
  ctx.fillText(accountName, WIDTH / 2, cursorY);

  cursorY += compact ? 55 : 70;
  ctx.fillStyle = TEXT_PRIMARY;
  fitFont(ctx, params.raffleName, WIDTH - MARGIN * 2, compact ? 54 : 64, "bold");
  ctx.fillText(params.raffleName, WIDTH / 2, cursorY);

  // Pastilla con los disponibles
  const takenCount = Array.from(params.taken).filter((ticket) => ticket < params.limit).length;
  const badgeText = `${params.limit - takenCount} disponibles de ${params.limit}`;
  const badgeHeight = compact ? 56 : 64;
  cursorY += compact ? 65 : 80;
  ctx.font = `bold ${compact ? 32 : 36}px sans-serif`;
  const badgeWidth = ctx.measureText(badgeText).width + 64;
  roundedRect(ctx, (WIDTH - badgeWidth) / 2, cursorY - badgeHeight / 2, badgeWidth, badgeHeight, badgeHeight / 2);
  ctx.fillStyle = palette.brand;
  ctx.fill();
  ctx.fillStyle = palette.onBrand;
  ctx.fillText(badgeText, WIDTH / 2, cursorY + 2);

  return cursorY + badgeHeight / 2;
};

const drawCard = (ctx: CanvasRenderingContext2D, card: Rect, palette: Palette) => {
  ctx.save();
  ctx.shadowColor = "rgba(0, 0, 0, 0.6)";
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 12;
  roundedRect(ctx, card.x, card.y, card.width, card.height, 36);
  ctx.fillStyle = CARD_FILL;
  ctx.fill();
  ctx.restore();

  roundedRect(ctx, card.x, card.y, card.width, card.height, 36);
  ctx.lineWidth = 2;
  ctx.strokeStyle = palette.subtle;
  ctx.stroke();
};

const drawLegend = (ctx: CanvasRenderingContext2D, centerY: number, palette: Palette) => {
  const swatch = 32;
  const availableX = WIDTH / 2 - 250;
  const takenX = WIDTH / 2 + 60;
  const swatchY = centerY - swatch / 2;

  ctx.save();
  ctx.textAlign = "left";
  ctx.font = "500 28px sans-serif";

  roundedRect(ctx, availableX, swatchY, swatch, swatch, 6);
  ctx.fillStyle = palette.cellFill;
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = palette.accent;
  ctx.stroke();
  ctx.fillStyle = TEXT_SECONDARY;
  ctx.fillText("Disponible", availableX + swatch + 14, centerY + 1);

  roundedRect(ctx, takenX, swatchY, swatch, swatch, 6);
  ctx.fillStyle = TAKEN_FILL;
  ctx.fill();
  strike(ctx, takenX, swatchY, swatch, swatch, 3);
  ctx.fillStyle = TEXT_SECONDARY;
  ctx.fillText("Tomado", takenX + swatch + 14, centerY + 1);
  ctx.restore();
};

interface CellStyle {
  fontSize: number;
  borderWidth: number;
  strikeWidth: number;
}

const drawTicketCell = (ctx: CanvasRenderingContext2D, cell: Rect, label: string, isTaken: boolean, style: CellStyle, palette: Palette) => {
  const { x, y, width, height } = cell;
  roundedRect(ctx, x, y, width, height, Math.min(12, width * 0.18));

  if (isTaken) {
    ctx.fillStyle = TAKEN_FILL;
    ctx.fill();
    ctx.font = `400 ${style.fontSize}px sans-serif`;
    ctx.fillStyle = TAKEN_TEXT;
    ctx.fillText(label, x + width / 2, y + height / 2 + 1);
    strike(ctx, x, y, width, height, style.strikeWidth);
    return;
  }

  ctx.fillStyle = palette.cellFill;
  ctx.fill();
  ctx.lineWidth = style.borderWidth;
  ctx.strokeStyle = palette.accent;
  ctx.stroke();
  ctx.font = `bold ${style.fontSize}px sans-serif`;
  ctx.fillStyle = TEXT_PRIMARY;
  ctx.fillText(label, x + width / 2, y + height / 2 + 1);
};

const drawGrid = (
  ctx: CanvasRenderingContext2D,
  grid: Rect,
  params: TalonarioImageParams,
  logo: HTMLImageElement | null,
  palette: Palette,
  compact: boolean,
) => {
  const columns = compact ? 25 : 10;
  const rows = Math.ceil(params.limit / columns);
  const cellWidth = grid.width / columns;
  const cellHeight = Math.min(grid.height / rows, cellWidth * 1.25);
  const offsetY = grid.y + (grid.height - cellHeight * rows) / 2;
  const padding = Math.max(2, cellWidth * 0.07);
  const style: CellStyle = compact
    ? { fontSize: Math.floor(Math.min(cellWidth, cellHeight) * 0.42), borderWidth: 1.5, strikeWidth: 2 }
    : { fontSize: Math.floor(Math.min(cellWidth, cellHeight) * 0.4), borderWidth: 3, strikeWidth: 4 };

  // Logo como marca de agua detrás de los números
  if (logo) {
    ctx.save();
    ctx.globalAlpha = 0.05;
    drawCircleImage(ctx, logo, WIDTH / 2, grid.y + grid.height / 2, Math.min(grid.width, grid.height) * 0.38);
    ctx.restore();
  }

  for (let ticket = 0; ticket < params.limit; ticket++) {
    const cell: Rect = {
      x: grid.x + (ticket % columns) * cellWidth + padding,
      y: offsetY + Math.floor(ticket / columns) * cellHeight + padding,
      width: cellWidth - padding * 2,
      height: cellHeight - padding * 2,
    };
    drawTicketCell(ctx, cell, params.formatTicket(ticket), params.taken.has(ticket), style, palette);
  }
};

const drawFooter = (ctx: CanvasRenderingContext2D, accountUrl: string, palette: Palette) => {
  const centerY = HEIGHT - FOOTER_HEIGHT / 2;
  ctx.fillStyle = palette.accent;
  ctx.font = "bold 46px sans-serif";
  ctx.fillText("¡Aparta tu número!", WIDTH / 2, centerY - 26);
  ctx.fillStyle = TEXT_SECONDARY;
  ctx.font = "500 32px sans-serif";
  ctx.fillText(accountUrl.replace(/^https?:\/\//, ""), WIDTH / 2, centerY + 32);
};

export const generateTalonarioImage = async (params: TalonarioImageParams): Promise<Blob> => {
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas no disponible");

  const palette = buildPalette(params.primaryColor || "#FFFFFF");
  const compact = params.limit > 100;
  const logo = await loadFirstImage(params.logoUrls ?? []);

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  drawBackground(ctx, palette);
  const headerBottom = drawHeader(ctx, params, logo, palette, compact);

  const cardTop = headerBottom + (compact ? 30 : 40);
  const card: Rect = { x: MARGIN, y: cardTop, width: WIDTH - MARGIN * 2, height: HEIGHT - FOOTER_HEIGHT - cardTop };
  drawCard(ctx, card, palette);
  drawLegend(ctx, card.y + card.height - LEGEND_HEIGHT / 2 - 5, palette);

  const gridPadding = compact ? 22 : 34;
  drawGrid(
    ctx,
    {
      x: card.x + gridPadding,
      y: card.y + gridPadding,
      width: card.width - gridPadding * 2,
      height: card.height - gridPadding * 2 - LEGEND_HEIGHT,
    },
    params,
    logo,
    palette,
    compact,
  );
  drawFooter(ctx, params.accountUrl, palette);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("No se pudo generar la imagen"))), "image/png");
  });
};
